import { WipPriorityE } from 'declarations/enums/wip';
import type { WipRecordI } from 'declarations/interfaces/wip';

import type { WipItemCellI } from './WIPItemsTableConfiguration';

export type WipEditableRowT = Partial<WipRecordI>;

const NON_EDITABLE_COLUMN_IDS = new Set<keyof WipRecordI>([
  'oemFamilyName',
  'wipStatus',
  'hasTickets',
  'createdAt',
  'updatedAt',
  'age',
]);

export interface ValidateWipRowResultI {
  valid: boolean;
  failedFieldIds: (keyof WipRecordI)[];
  message: string;
}

const isEditableInRow = (columnId: keyof WipRecordI, isNewRow: boolean): boolean => {
  if (NON_EDITABLE_COLUMN_IDS.has(columnId)) return false;
  if (!isNewRow && (columnId === 'repairOrderNumber' || columnId === 'roofTopName')) return false;
  return true;
};

const isEmpty = (payload: WipEditableRowT, columnId: keyof WipRecordI): boolean => {
  if (columnId === 'roofTopName') {
    const roofTopId = payload.roofTopId ?? '';
    return String(roofTopId).trim() === '';
  }
  if (
    columnId === 'submittedDate' ||
    columnId === 'openRepairOrderDate' ||
    columnId === 'oemPaidDate'
  ) {
    const value = payload[columnId];
    return value == null || String(value).trim() === '';
  }
  if (columnId === 'advisorNumber' || columnId === 'techNumber') {
    const value = payload[columnId];
    return value == null || (typeof value === 'string' && value.trim() === '');
  }
  if (columnId === 'priority') {
    const value = payload.priority;
    return value == null || value === WipPriorityE.Undefined;
  }
  if (columnId === 'backlog') {
    return payload.backlog === undefined;
  }
  const value = payload[columnId];
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
};

/**
 * Validates the WIP entry payload per the Chirp WIP Field Matrix (WARR-10507):
 *  - Only `repairOrderNumber` is statically required.
 *  - `submittedDate` is conditionally required when Claim Status === "Submitted"; this check
 *    fires regardless of whether the column is currently visible so the user can never bypass
 *    the rule by hiding the column.
 *  - When the OEM-specific claim-status list is known, the value must be one of the allowed
 *    options.
 *
 * The error message intentionally matches the Chirp WIP spec verbatim
 * (WARR-10277 — "Unable to add item to WIP. Please complete required fields.") so the toast
 * text is identical to the design.
 */
export const validateWipRow = (
  payload: WipEditableRowT,
  visibleColumns: WipItemCellI[],
  isNewRow: boolean,
  allowedClaimStatuses?: string[]
): ValidateWipRowResultI => {
  const failedFieldIds: (keyof WipRecordI)[] = [];

  if (
    allowedClaimStatuses != null &&
    allowedClaimStatuses.length > 0 &&
    payload.oemClaimStatus != null &&
    String(payload.oemClaimStatus).trim() !== ''
  ) {
    const value = payload.oemClaimStatus.trim();
    if (!allowedClaimStatuses.includes(value)) {
      failedFieldIds.push('oemClaimStatus');
    }
  }

  for (const column of visibleColumns) {
    if (!isEditableInRow(column.id, isNewRow)) continue;
    if (column.required !== true) continue;
    if (isEmpty(payload, column.id)) {
      failedFieldIds.push(column.id);
    }
  }

  /**
   * Conditional rule that lives outside the column loop so it applies even when the user
   * has hidden the Submitted Date column via the column picker.
   */
  if (
    payload.oemClaimStatus?.trim() === 'Submitted' &&
    isEmpty(payload, 'submittedDate') &&
    !failedFieldIds.includes('submittedDate')
  ) {
    failedFieldIds.push('submittedDate');
  }

  const valid = failedFieldIds.length === 0;
  const message = valid ? '' : 'Unable to add item to WIP. Please complete required fields.';

  return { valid, failedFieldIds, message };
};
